import React, { useEffect, useMemo, useState } from 'react';
import { isMobileOnly } from 'mobile-device-detect';
import { Modal, Form, Button } from 'semantic-ui-react';
import { useTextResources } from '../../language';
import EntityTypesDropDown from '../../components/shared/EntityTypesDropDown';
import TagsDropDown from '../../components/shared/TagsDropDown';
import DynamicEntityDropDown from '../../components/shared/DynamicEntityDropDown';
import HotelsDropDown from '../../components/shared/HotelsDropDown';
import PropTypes from 'prop-types';
import { EntityType as EntityTypes, OperationsEnum } from '../../components/shared/constants';
import { Rooms } from '../../api/services/next';
import { onError } from '../../components/shared/messages';
import uniqid from 'uniqid';

export const RowEditModal = ({
  extendedProps: {
    hotelsFromApi,
    roomTypesFromApi,
    tagsFromApi,
    onAddTag,
    onDeleteRow,
  },
  row,
  onChange,
  onApplyChanges,
  onCancelChanges,
  open,
}) => {
  const text = useTextResources();

  const [roomsFromApi, setRoomsFromApi] = useState([]);

  useEffect(() => {
    //Not using the hotels from Tags as they need to change overtime due to HotelId changes
    try {
      Rooms.GetRooms({ MaxResults: 1000, HotelId: row.HotelId }).then(
        setRoomsFromApi
      );
    } catch (error) {
      onError(error);
    }
  }, [row.HotelId]);

  const dynamicEntityDropDownData = ({ EntityTypeId }) => {
    return {
      [EntityTypes.Room]: roomsFromApi.filter(
        (room) => room.HotelId === row.HotelId
      ), //Show only rooms of chosen hotel
      [EntityTypes.RoomType]: roomTypesFromApi,
    }[EntityTypeId];
  };

  const dynamicTagsData = ({ EntityTypeId, HotelId }) => {
    return {
      [EntityTypes.Room]: tagsFromApi.filter(
        (r) => r.EntityTypeId === EntityTypes.Room && r.HotelId === HotelId
        //Room is hotel level
      ),
      [EntityTypes.RoomType]: tagsFromApi.filter(
        (r) => r.EntityTypeId === EntityTypes.RoomType
      ),
    }[EntityTypeId];
  };

  const dynamicEntityLabel = ({ EntityTypeId }) => {
    return {
      [EntityTypes.Room]: text('Rooms'),
      [EntityTypes.RoomType]: text('RoomTypes'),
    }[EntityTypeId];
  };

  const isSaveDisabled = () => {
    //Room has to be accompanied by HotelId
    if (isEntityTypeHotelLevel && !row.HotelId) return true;
    //The below fields must be filled
    return !(
      row.EntityId &&
      row.EntityTypeId &&
      row.Tags &&
      Object.values(row.Tags)?.length
    );
  };

  const changeEntityTypeHandler = (value) => {
    onChange('EntityTypeId', value);
    onChange('EntityId', null);
    if (!isEntityTypeHotelLevel) {
      //EntityType is chain-level - remove hotel from state
      onChange('HotelId', null);
    }
  };

  const getKeyByValue = (object, value) =>
    Object.keys(object).find((key) => object[key] === value);

  const changeTagsHandler = (tagIds) => {
    if (tagIds.some((id) => typeof id === 'string')) return;
    //In the case we added a new tag, entity hasn't been created, no reason to get inside the function

    let operation;
    let tags = { ...row?.Tags };
    const rowTagIds = Object.values(tags || []);

    const deletedTagId = rowTagIds.find((id) => !tagIds.includes(id));
    const addedTagId = tagIds.find((id) => !rowTagIds.includes(id));

    if (tagIds.length > rowTagIds.length) {
      operation = OperationsEnum.ADD;
    } else if (rowTagIds.length > tagIds.length) {
      operation = OperationsEnum.REMOVE;
    }

    if (operation == OperationsEnum.ADD) {
      tags = { ...tags, ...{ [uniqid('newRow-')]: addedTagId } };
    }

    if (operation == OperationsEnum.REMOVE) {
      const deletedRowId = getKeyByValue(tags, deletedTagId);
      onDeleteRow((prev) => [...prev, deletedRowId]); //Adds to array of deleted ids
      Reflect.deleteProperty(tags, deletedRowId);
    }
    onChange('Tags', tags);
  };

  const tag = {
    IsSystem: false,
    ChainId: window.optima.user.HotelChainId || hotelsFromApi[0].HotelChainId,
    HotelId: row.HotelId,
    EntityTypeId: row.EntityTypeId,
  };

  /*Some entity types are chain-level and some hotel-level. 
    In the case of adding new entityTypes, we need to also add the
    types to EntityTypesByHotelLevel array at the bottom of the page.*/
  const isEntityTypeHotelLevel = useMemo(
    () => EntityTypesByHotelLevel.some((typeId) => row.EntityTypeId == typeId),
    [row.EntityTypeId]
  );

  const addTagHandler = (newTag) => {
    onAddTag(newTag);
    //Update the tags state on Tags page so other rows will be aware of the changes in the lack of API call
    onChange('Tags', { ...row.Tags, ...{ [uniqid('newRow-')]: newTag.Id } });
    //Update the modal's row
  };

  return (
    <Modal
      size={isMobileOnly ? 'fullscreen' : 'tiny'}
      className={isMobileOnly ? 'mobile' : ''}
      open={open}
      style={{ zIndex: 1000000 }}
    >
      {!isMobileOnly && <Modal.Header>{text('TagsRowUpdate')}</Modal.Header>}
      <Modal.Content
        style={isMobileOnly ? { overflowY: 'auto' } : { maxHeight: 600 }}
      >
        <Form size='small'>
          <Form.Group
            unstackable
            widths={'equal'}
            style={{ flexWrap: 'wrap', gap: '1em' }}
          >
            <Form.Field
              label={`${text('EntityType')}`}
              fluid
              entityTypeIds={[EntityTypes.Room, EntityTypes.RoomType]}
              control={EntityTypesDropDown}
              value={row.EntityTypeId}
              onChange={changeEntityTypeHandler}
              required
            />
            <Form.Field
              label={`${text('Hotel')}`}
              fluid
              data={hotelsFromApi}
              control={HotelsDropDown}
              value={row.HotelId}
              clearable
              onChange={onChange.bind(null, 'HotelId')}
              required={isEntityTypeHotelLevel}
              //Room is on the hotel-level and thus when room is chosen, hotel must be chosen
              disabled={!isEntityTypeHotelLevel}
              //Room-type is on the chain-level and thus when room is chosen, hotel cannot be chosen
            />
            <Form.Field
              label={dynamicEntityLabel(row)}
              fluid
              control={DynamicEntityDropDown}
              EntityTypeId={row.EntityTypeId}
              value={row.EntityId}
              disabled={!row.EntityTypeId}
              data={dynamicEntityDropDownData(row)}
              clearable
              onChange={onChange.bind(null, 'EntityId')}
              required
            />
            <Form.Field
              label={`${text('Tags')}`}
              fluid
              control={TagsDropDown}
              value={Object.values(row.Tags || [])}
              data={dynamicTagsData(row)}
              clearable
              multiple
              onAddTag={addTagHandler}
              onChange={changeTagsHandler}
              required
              {...tag}
            />
          </Form.Group>
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button
          size={isMobileOnly ? 'tiny' : 'small'}
          negative
          onClick={onCancelChanges}
        >
          {text('Cancel')}
        </Button>
        <Button
          size={isMobileOnly ? 'tiny' : 'small'}
          disabled={isSaveDisabled()}
          positive
          onClick={onApplyChanges}
        >
          {text('OK')}
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

const EntityTypesByHotelLevel = [EntityTypes.Room];

RowEditModal.propTypes = {
  extendedProps: PropTypes.object,
  row: PropTypes.object,
  onChange: PropTypes.func,
  onApplyChanges: PropTypes.func,
  onCancelChanges: PropTypes.func,
  open: PropTypes.bool,
  onDeleteRow: PropTypes.func,
};
